import React, {useCallback, useEffect, useMemo, useRef, useState, ReactNode} from 'react';
import {Button, Form, Modal} from 'react-bootstrap';
import axios from './axiosConfig';

import {Responsive, WidthProvider, Layout, Layouts} from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import PanelCard from './PanelCard';

import GaugeChart from './GaugeChart';
import LineChartPanel from './LineChartPanel';
import CustomTable from './CustomTable';
import SolidColorCard from './SolidColorCard';
import UsageProgressBar from './UsageProgressBar';
import TraceroutePanel from './TraceroutePanel';
import TimeSeriesChart from './TimeSeriesChart';

import './PanelDisplay.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface PanelItem {
    id?: number;
    dashboard_id?: number;

    order: number;
    type: string;
    data?: any;

    width?: number;
    height?: number;
    position_x?: number;
    position_y?: number;

    i?: string;

    oid?: string;
    metric?: string;
    collect_method?: string;
    snmp_credential_id?: number;
    source?: string;
    destination?: string;

    time?: string[];
    series?: { name: string; data: number[] }[];
}

interface PanelDisplayProps {
    selectedPanels: PanelItem[];
    onRemovePanel: (arg: any) => void;
    onUpdatePanels: (newPanels: any[]) => void;

    enableSaveAsDashboard?: boolean;
    extraHeaderActions?: ReactNode;
    headerTitle?: string;

    defaultEditMode?: boolean;
    timezone?: string;
}

const panelComponents: { [key: string]: React.FC<any> } = {
    GaugeChart,

    TopologyPanel: TraceroutePanel,
    TraceroutePanel: TraceroutePanel,

    LineChart: LineChartPanel,
    CustomTable,
    SolidColorCard,
    UsageProgressBar,

    TimeSeriesChart,
    BandwidthChart: TimeSeriesChart,
};

const DEFAULT_COLS = {lg: 24, md: 16, sm: 12, xs: 8, xxs: 4};
const BREAKPOINTS = {lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0};
const ROW_HEIGHT = 10;

const GRID_MARGIN: [number, number] = [10, 10];
const GRID_PADDING: [number, number] = [8, 8];

const MIN_W_PX = 140;
const MIN_H_PX = 120;
const MAX_W_PX = 2000;
const MAX_H_PX = 1400;

function clamp(n: number, min: number, max: number) {
    if (Number.isNaN(n)) return min;
    return Math.max(min, Math.min(max, n));
}

const isFiniteNumber = (v: any) => Number.isFinite(Number(v));

const looksLikePx = (p: PanelItem) => {
    const w = Number(p.width ?? 0);
    const x = Number(p.position_x ?? 0);
    const h = Number(p.height ?? 0);
    const y = Number(p.position_y ?? 0);

    return (
        (isFiniteNumber(w) && w > 60) ||
        (isFiniteNumber(x) && x > 60) ||
        (isFiniteNumber(h) && h > 200) ||
        (isFiniteNumber(y) && y > 200)
    );
};

function getColWidth(containerWidth: number, cols: number, marginX: number, paddingX: number) {
    const usable = containerWidth - paddingX * 2 - marginX * (cols - 1);
    return usable > 0 ? usable / cols : containerWidth / Math.max(1, cols);
}

function gridWToPx(w: number, colWidth: number, marginX: number) {
    return w * colWidth + Math.max(0, w - 1) * marginX;
}

function gridHToPx(h: number, rowHeight: number, marginY: number) {
    return h * rowHeight + Math.max(0, h - 1) * marginY;
}

function pxToGridW(widthPx: number, colWidth: number, marginX: number, cols: number) {
    const w = Math.round((widthPx + marginX) / (colWidth + marginX));
    return clamp(w, 1, cols);
}

function pxToGridH(heightPx: number, rowHeight: number, marginY: number) {
    const h = Math.round((heightPx + marginY) / (rowHeight + marginY));
    return clamp(h, 1, 10000);
}

function getStablePanelId(panel: PanelItem, index: number) {
    if (panel.i && panel.i.trim()) return panel.i.trim();
    if (isFiniteNumber(panel.id)) return `panel-${Number(panel.id)}`;
    return `panel-auto-${panel.type}-${panel.order ?? index}-${index}`;
}

function buildInitialLayouts(panels: PanelItem[]): Layouts {
    const colsLg = DEFAULT_COLS.lg;

    const lg: Layout[] = panels.map((p, idx) => {
        const i = getStablePanelId(p, idx);

        const w = clamp(Math.round(Number(p.width ?? 6)), 3, colsLg);
        const h = clamp(Math.round(Number(p.height ?? 12)), 10, 10000);
        const x = clamp(Math.round(Number(p.position_x ?? ((idx * 6) % colsLg))), 0, Math.max(0, colsLg - w));
        const y = clamp(Math.round(Number(p.position_y ?? 0)), 0, 100000);

        return {i, x, y, w, h, minW: 3, minH: 10};
    });

    return {lg};
}

function safeParsePanelData(raw: any): any {
    if (raw === null || raw === undefined) return {};
    if (typeof raw === 'object') return raw;

    if (typeof raw === 'string') {
        const s = raw.trim();
        if (!s) return {};
        try {
            return JSON.parse(s);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to parse panel.data JSON:', e);
            return {};
        }
    }

    return {};
}

function stringifyPanelData(data: any): string {
    if (data === null || data === undefined) return '{}';
    if (typeof data === 'string') return data;

    try {
        return JSON.stringify(data);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to stringify panel.data:', e);
        return '{}';
    }
}

function safeTrim(s: string) {
    return (s ?? '').trim();
}

function parseTargetFromApiEndpoint(input: string): string {
    const raw = safeTrim(input);
    if (!raw) return '';

    try {
        const u = new URL(raw, window.location.origin);
        return safeTrim(u.searchParams.get('target') || '');
    } catch {
        const idx = raw.indexOf('target=');
        if (idx === -1) return '';
        const rest = raw.substring(idx + 'target='.length);
        const end = rest.indexOf('&');
        try {
            return safeTrim(decodeURIComponent(end === -1 ? rest : rest.substring(0, end)));
        } catch {
            return safeTrim(end === -1 ? rest : rest.substring(0, end));
        }
    }
}

function isTrafficPromql(q: string): boolean {
    const s = safeTrim(q).toLowerCase();

    if (s.includes('octets') && (s.includes('*8') || s.includes('* 8'))) return true;
    if (s.includes('ifhcin') || s.includes('ifhcout')) return true;
    if (s.includes('bandwidth') || s.includes('throughput')) return true;

    return false;
}

function ensureTimeSeriesUnitRows(rows: any, fallbackUnit: string) {
    if (!Array.isArray(rows)) return rows;
    return rows.map((row: any) => {
        if (!Array.isArray(row)) return row;
        const ts = row[0];
        const val = row[1];
        const unit = safeTrim(String(row[2] ?? ''));
        return [ts, val, unit || fallbackUnit];
    });
}

const PanelDisplay: React.FC<PanelDisplayProps> = ({
                                                       selectedPanels,
                                                       onRemovePanel,
                                                       onUpdatePanels,
                                                       enableSaveAsDashboard = true,
                                                       extraHeaderActions,
                                                       headerTitle = 'Selected Panels',
                                                       defaultEditMode = true,
                                                       timezone: timezoneProp,
                                                   }) => {
    const effectiveTimezone = (timezoneProp || '').trim() || 'UTC';
    const [editMode, setEditMode] = useState<boolean>(defaultEditMode);

    const [message, setMessage] = useState<string | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [folders, setFolders] = useState<string[]>([]);
    const [selectedFolder, setSelectedFolder] = useState<string>('');
    const [newFolderName, setNewFolderName] = useState<string>('');
    const [dashboardName, setDashboardName] = useState<string>('');
    const [modalMessage, setModalMessage] = useState<string | null>(null);

    const [gridWidth, setGridWidth] = useState<number>(1200);
    const [currentCols, setCurrentCols] = useState<number>(DEFAULT_COLS.lg);

    const [layouts, setLayouts] = useState<Layouts>(() => buildInitialLayouts(selectedPanels));

    const lastIdSigRef = useRef<string>('');
    const migratedRef = useRef<boolean>(false);

    useEffect(() => {
        const sig = selectedPanels
            .map((p, idx) => getStablePanelId(p, idx))
            .sort()
            .join('|');

        if (!lastIdSigRef.current) {
            lastIdSigRef.current = sig;
            setLayouts(buildInitialLayouts(selectedPanels));
            return;
        }

        if (lastIdSigRef.current !== sig) {
            lastIdSigRef.current = sig;

            setLayouts((prev) => {
                const next = buildInitialLayouts(selectedPanels);

                const prevLg = prev?.lg ?? [];
                const prevMap = new Map(prevLg.map((l) => [l.i, l] as const));

                const mergedLg = (next.lg ?? []).map((l) => {
                    const old = prevMap.get(l.i);
                    return old ? {...l, x: old.x, y: old.y, w: old.w, h: old.h} : l;
                });

                return {...next, lg: mergedLg};
            });
        }
    }, [selectedPanels]);

    useEffect(() => {
        if (migratedRef.current) return;
        if (!selectedPanels || selectedPanels.length === 0) return;

        if (!gridWidth || gridWidth <= 0 || !currentCols || currentCols <= 0) return;

        const hasPx = selectedPanels.some(looksLikePx);
        if (!hasPx) {
            migratedRef.current = true;
            return;
        }

        const colWidthLocal = getColWidth(gridWidth, currentCols, GRID_MARGIN[0], GRID_PADDING[0]);

        const migrated = selectedPanels.map((p, idx) => {
            const i = getStablePanelId(p, idx);
            if (!looksLikePx(p)) return {...p, i};

            const widthPx = Number(p.width ?? 300);
            const heightPx = Number(p.height ?? 200);
            const xPx = Number(p.position_x ?? 0);
            const yPx = Number(p.position_y ?? 0);

            const w = pxToGridW(clamp(widthPx, MIN_W_PX, MAX_W_PX), colWidthLocal, GRID_MARGIN[0], currentCols);
            const h = pxToGridH(clamp(heightPx, MIN_H_PX, MAX_H_PX), ROW_HEIGHT, GRID_MARGIN[1]);
            const x = clamp(Math.round(xPx / (colWidthLocal + GRID_MARGIN[0])), 0, Math.max(0, currentCols - w));
            const y = clamp(Math.round(yPx / (ROW_HEIGHT + GRID_MARGIN[1])), 0, 100000);

            return {...p, i, width: w, height: h, position_x: x, position_y: y};
        });

        migratedRef.current = true;
        onUpdatePanels(migrated);
        setLayouts(buildInitialLayouts(migrated));
    }, [gridWidth, currentCols, selectedPanels, onUpdatePanels]);

    useEffect(() => {
        if (!enableSaveAsDashboard) return;

        const fetchFolders = async () => {
            try {
                const response = await axios.get('/api/folders');
                const data = response.data;

                if (data && Array.isArray(data.folders)) {
                    setFolders(data.folders.map((folder: any) => folder.name));
                } else {
                    setFolders([]);
                }
            } catch {
                setFolders([]);
            }
        };

        fetchFolders();
    }, [enableSaveAsDashboard]);

    const createFolder = async (folderName: string) => {
        try {
            await axios.post('/api/create-folder', {folderName});
            setFolders((prev) => [...prev, folderName]);
            setSelectedFolder(folderName);
        } catch {
            setModalMessage('Error creating folder. Please try again.');
        }
    };

    const getLgLayoutMap = useMemo(() => {
        const lg = layouts?.lg ?? [];
        const map = new Map<string, Layout>();
        lg.forEach((l) => map.set(l.i, l));
        return map;
    }, [layouts]);

    const applyLayoutToPanels = (nextLayouts: Layouts) => {
        const lg = nextLayouts.lg ?? [];
        const layoutMap = new Map<string, Layout>();
        lg.forEach((l) => layoutMap.set(l.i, l));

        const updated = selectedPanels.map((p, idx) => {
            const i = getStablePanelId(p, idx);
            const l = layoutMap.get(i);
            if (!l) return {...p, i};

            return {
                ...p,
                i,
                order: p.order ?? idx,
                width: l.w,
                height: l.h,
                position_x: l.x,
                position_y: l.y,
            };
        });

        onUpdatePanels(updated);
    };

    const handleLayoutChange = (_current: Layout[], all: Layouts) => {
        setLayouts(all);
        if (editMode) applyLayoutToPanels(all);
    };

    const handleWidthHeightInput = (panelIndex: number, newWidthPx?: number, newHeightPx?: number) => {
        const panel = selectedPanels[panelIndex];
        if (!panel) return;

        const i = getStablePanelId(panel, panelIndex);

        const lg = [...(layouts.lg ?? [])];
        const idx = lg.findIndex((l) => l.i === i);
        if (idx < 0) return;

        const colWidthLocal = getColWidth(gridWidth, currentCols, GRID_MARGIN[0], GRID_PADDING[0]);
        const next = {...lg[idx]};

        if (typeof newWidthPx === 'number') {
            const safeWpx = clamp(newWidthPx, MIN_W_PX, MAX_W_PX);
            next.w = pxToGridW(safeWpx, colWidthLocal, GRID_MARGIN[0], currentCols);
        }

        if (typeof newHeightPx === 'number') {
            const safeHpx = clamp(newHeightPx, MIN_H_PX, MAX_H_PX);
            next.h = pxToGridH(safeHpx, ROW_HEIGHT, GRID_MARGIN[1]);
            next.h = Math.max(next.h, 10);
        }

        lg[idx] = next;

        const nextLayouts: Layouts = {...layouts, lg};
        setLayouts(nextLayouts);
        if (editMode) applyLayoutToPanels(nextLayouts);
    };

    const updatePanelDataByKey = useCallback(
        (panelKey: string, nextData: any) => {
            const updated = selectedPanels.map((p, idx) => {
                const key = getStablePanelId(p, idx);
                if (key !== panelKey) return p;
                return {...p, data: nextData};
            });
            onUpdatePanels(updated);
        },
        [selectedPanels, onUpdatePanels],
    );

    const handleSaveClick = () => {
        if (!enableSaveAsDashboard) return;

        if (selectedPanels.length === 0) {
            setMessage('Please select at least one panel before saving.');
            return;
        }
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setModalMessage(null);
    };

    const handleSaveAsDashboard = async () => {
        if (!enableSaveAsDashboard) return;

        if (!selectedFolder && !newFolderName) {
            setModalMessage('Please select an existing folder or create a new one.');
            return;
        }

        if (!dashboardName) {
            setModalMessage('Please enter a name for the dashboard.');
            return;
        }

        try {
            let folder = selectedFolder;

            if (newFolderName) {
                await createFolder(newFolderName);
                folder = newFolderName;
            }

            const panelsWithDimensions = selectedPanels.map((panel, index) => {
                const stableI = getStablePanelId(panel, index);

                return {
                    ...panel,
                    i: stableI,
                    data: stringifyPanelData(panel.data),
                    width: isFiniteNumber(panel.width) ? Math.round(Number(panel.width)) : 6,
                    height: isFiniteNumber(panel.height) ? Math.round(Number(panel.height)) : 12,
                    position_x: isFiniteNumber(panel.position_x) ? Math.round(Number(panel.position_x)) : 0,
                    position_y: isFiniteNumber(panel.position_y) ? Math.round(Number(panel.position_y)) : 0,
                    order: panel.order ?? index,
                };
            });

            const requestData = {folderName: folder, dashboardName, panels: panelsWithDimensions};
            const response = await axios.post('/api/save-dashboard', requestData);

            if (response.status === 200) {
                setMessage('Dashboard saved successfully!');
                setShowModal(false);
            } else {
                throw new Error('Failed to save dashboard');
            }
        } catch {
            setMessage('Error saving dashboard. Please try again.');
        }
    };

    const colWidth = getColWidth(gridWidth, currentCols, GRID_MARGIN[0], GRID_PADDING[0]);

    return (
        <div className="panel-display-root">
            <div className="panel-display-header">
                {headerTitle && (
                    <div className="panel-display-header-left">
                        <div className="panel-display-title">{headerTitle}</div>
                    </div>
                )}

                <div className="panel-display-header-actions">
                    {extraHeaderActions}

                    <Button
                        variant={editMode ? 'primary' : 'outline-secondary'}
                        size="sm"
                        onClick={() => setEditMode((v) => !v)}
                        title="Toggle edit mode"
                    >
                        {editMode ? 'Editing' : 'View'}
                    </Button>

                    {enableSaveAsDashboard && (
                        <>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleSaveClick}
                                disabled={selectedPanels.length === 0}
                                title="Save as dashboard"
                            >
                                Save
                            </Button>

                            {message && <span className="panel-display-header-message">{message}</span>}
                        </>
                    )}
                </div>
            </div>

            <div className="panel-display-canvas">
                <ResponsiveGridLayout
                    className="layout"
                    layouts={layouts}
                    cols={DEFAULT_COLS}
                    breakpoints={BREAKPOINTS}
                    rowHeight={ROW_HEIGHT}
                    margin={GRID_MARGIN}
                    containerPadding={GRID_PADDING}
                    compactType={null}
                    preventCollision={false}
                    isDraggable={editMode}
                    isResizable={editMode}
                    draggableHandle=".panel-card-header-drag-handle"
                    draggableCancel=".panel-remove-btn, .panel-card-size-input, .panel-card-size-input *"
                    resizeHandles={editMode ? ['se', 'e', 's'] : []}
                    useCSSTransforms={true}
                    onLayoutChange={handleLayoutChange}
                    onWidthChange={(width, _margin, cols) => {
                        setGridWidth(width);
                        setCurrentCols(cols);
                    }}
                >
                    {selectedPanels.map((panel, index) => {
                        const i = getStablePanelId(panel, index);
                        const PanelComponent = panelComponents[panel.type];

                        const parsedData = safeParsePanelData(panel.data);

                        const isTraceroute = panel.type === 'TraceroutePanel' || panel.type === 'TopologyPanel';

                        const derivedTarget = isTraceroute
                            ? (safeTrim(parsedData?.target) ||
                                parseTargetFromApiEndpoint(String(parsedData?.apiEndpoint || parsedData?.endpoint || '')) ||
                                safeTrim(parsedData?.defaultTarget) ||
                                safeTrim(parsedData?.source) ||
                                'google.com')
                            : '';

                        const tracerouteData = isTraceroute
                            ? {
                                ...parsedData,
                                target: derivedTarget,
                            }
                            : parsedData;

                        const title =
                            tracerouteData.title ||
                            tracerouteData.name ||
                            (isTraceroute ? `Traceroute to ${derivedTarget}` : panel.type || i);

                        const layoutInfo = getLgLayoutMap.get(i);

                        const wGrid = layoutInfo?.w ?? clamp(Math.round(Number(panel.width ?? 6)), 3, currentCols);
                        const hGrid = layoutInfo?.h ?? clamp(Math.round(Number(panel.height ?? 12)), 10, 10000);

                        const derivedWidthPx = clamp(
                            Math.round(gridWToPx(wGrid, colWidth, GRID_MARGIN[0])),
                            MIN_W_PX,
                            MAX_W_PX
                        );

                        const derivedHeightPx = clamp(
                            Math.round(gridHToPx(hGrid, ROW_HEIGHT, GRID_MARGIN[1])),
                            MIN_H_PX,
                            MAX_H_PX
                        );

                        const dataGrid: Layout = layoutInfo ?? {
                            i,
                            x: clamp(
                                Math.round(Number(panel.position_x ?? ((index * 6) % currentCols))),
                                0,
                                Math.max(0, currentCols - wGrid)
                            ),
                            y: clamp(Math.round(Number(panel.position_y ?? 0)), 0, 100000),
                            w: wGrid,
                            h: hGrid,
                            minW: 3,
                            minH: 10,
                        };

                        const removeArg = isFiniteNumber(panel.id) ? Number(panel.id) : index;

                        return (
                            <div key={i} data-grid={dataGrid}>
                                <PanelCard
                                    title={title}
                                    onRemove={editMode ? () => onRemovePanel(removeArg) : undefined}
                                    showSizeControls={editMode}
                                    width={derivedWidthPx}
                                    height={derivedHeightPx}
                                    onWidthChange={(w) => handleWidthHeightInput(index, w, undefined)}
                                    onHeightChange={(h) => handleWidthHeightInput(index, undefined, h)}
                                    draggablePropsStyle={{width: '100%', height: '100%'}}
                                >
                                    {PanelComponent ? (
                                        (() => {
                                            if (isTraceroute) {
                                                const propsForTraceroute: any = {
                                                    style: {width: '100%', height: '100%'},

                                                    defaultTarget: derivedTarget,
                                                    apiEndpoint: tracerouteData?.apiEndpoint || tracerouteData?.endpoint,

                                                    onPanelDataChange: (next: any) => updatePanelDataByKey(i, next),

                                                    data: tracerouteData,
                                                };

                                                return <PanelComponent {...propsForTraceroute} />;
                                            }

                                            if (panel.data === undefined || panel.data === null) {
                                                return <div className="panel-card-empty">No data available for this
                                                    panel</div>;
                                            }

                                            const isTimeSeries =
                                                panel.type === 'TimeSeriesChart' ||
                                                panel.type === 'BandwidthChart' ||
                                                PanelComponent === TimeSeriesChart;

                                            if (isTimeSeries) {
                                                const promql = safeTrim(String(parsedData?.valueQuery || parsedData?.value_query || ''));
                                                const trafficByQuery = isTrafficPromql(promql);

                                                const autoUnit =
                                                    parsedData?.autoUnit !== undefined ? !!parsedData.autoUnit : trafficByQuery;

                                                const nextRows = autoUnit
                                                    ? ensureTimeSeriesUnitRows(parsedData?.data, 'bps')
                                                    : parsedData?.data;

                                                return (
                                                    <TimeSeriesChart
                                                        title={parsedData?.title || title}
                                                        data={nextRows || []}
                                                        autoUnit={autoUnit}
                                                        style={{width: '100%', height: '100%'}}
                                                        timezone={effectiveTimezone}
                                                    />
                                                );
                                            }

                                            return <PanelComponent {...parsedData}
                                                                   style={{width: '100%', height: '100%'}}/>;
                                        })()
                                    ) : (
                                        <div className="panel-card-empty">Unknown panel type: {panel.type}</div>
                                    )}
                                </PanelCard>
                            </div>
                        );
                    })}
                </ResponsiveGridLayout>
            </div>

            {enableSaveAsDashboard && (
                <Modal show={showModal} onHide={handleModalClose} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Select or Create Folder and Name Your Dashboard</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {modalMessage && <p style={{color: 'red', fontWeight: 'bold'}}>{modalMessage}</p>}

                        <Form.Group controlId="dashboardNameInput">
                            <Form.Label>Enter a name for your dashboard:</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter dashboard name"
                                value={dashboardName}
                                onChange={(e) => setDashboardName(e.target.value)}
                            />
                        </Form.Group>

                        <hr/>

                        <Form.Group controlId="existingFolderSelect">
                            <Form.Label>Select an existing folder:</Form.Label>
                            <Form.Select value={selectedFolder} onChange={(e) => setSelectedFolder(e.target.value)}>
                                <option value="">-- Select a folder --</option>
                                {(folders || []).map((folder, idx) => (
                                    <option key={idx} value={folder}>
                                        {folder}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <hr/>

                        <Form.Group controlId="newFolderInput">
                            <Form.Label>Or create a new folder:</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter new folder name"
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                            />
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleModalClose}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSaveAsDashboard}>
                            Save as Dashboard
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
};

export default PanelDisplay;
